import { useState, useMemo, useEffect, useContext, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";

import TableComponent from "ui/ui-kit/react-table/TableComponent";
import WireManagementStateController from "./state-controller/WireManagementStateController";
import WireConfirmationModal from "./WireConfirmationModal";
import WireDetailsModal from "./WireDetailsModal";
import PayoutRequestModal from "./PayoutRequestModal";
import AutoPayoutSettingModal from "./AutoPayoutSettingModal";
import DisableAutoPayoutModal from "./DisableAutoPayoutModal";
import ActivePayoutModal from "./ActivePayoutModal";
import { ModalContext } from "ui/ui-kit/modal/ModalContext";
import useStatistic from "ui/hooks/useStatistic";
import { accountThunks } from "actions-thunks/account-thunks";
import { payoutThunks } from "actions-thunks/payout-thunks";
import { TO_BANK_ACCOUNT_DETAILS } from "reducers/types";
import AutomaticPayoutWidget from "./components/AutomaticPayoutWidget";
import DepositConfirmationWidget from "./components/DepositConfirmationWidget";
import RequestPayoutWidget from "./components/RequestPayoutWidget";
import BankDetailsWidget from "./components/BankDetailsWidget";
import {
  formatWireAmount,
  formatWireDate,
  getActiveManualPayout,
  getCurrentAutoPayout,
  getLastDeclinedAutoPayout,
  getPendingDeposits,
  hasActivePayout,
  getWireAmount,
  getWireCreatedDate,
  getWireStatusDisplay,
  getWireTypeDisplay,
} from "./wireUtils";

import "./wire-management.scss";

const PAGE_SIZE = 10;
const listParams = (page = 1, pageLen = PAGE_SIZE) => ({ page, pageLen, sort: "-requestedOn" });

const WireManagementPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading } = useSelector((state) => state.utils);
  const { currentAccount, wiresList, wiresListTotal, payoutSettings, paymentMethods, extensions } =
    useSelector((state) => state.accounts);
  const [showWireConfirmationModal, setShowWireConfirmationModal] = useState(false);
  const [showPayoutRequestModal, setShowPayoutRequestModal] = useState(false);
  const [showAutoPayoutModal, setShowAutoPayoutModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [activePayout, setActivePayout] = useState(null);
  const [page, setPage] = useState(1);
  const { showModal, hideModal } = useContext(ModalContext);

  useStatistic(currentAccount?._id, "Open page Wire Management");

  const refreshWires = useCallback(() => {
    if (currentAccount?._id) {
      setPage(1);
      dispatch(payoutThunks.loadWiresWithPayoutsTC(currentAccount._id, listParams(1)));
    }
  }, [currentAccount, dispatch]);

  const handleShowMore = useCallback(() => {
    if (!currentAccount?._id) return;
    const nextPage = page + 1;
    setPage(nextPage);
    dispatch(
      payoutThunks.loadWiresWithPayoutsTC(currentAccount._id, listParams(nextPage), { append: true })
    );
  }, [currentAccount, dispatch, page]);

  const handleShowAll = useCallback(() => {
    if (!currentAccount?._id || !wiresListTotal) return;
    dispatch(payoutThunks.loadWiresWithPayoutsTC(currentAccount._id, listParams(1, wiresListTotal)));
    setPage(Math.ceil(wiresListTotal / PAGE_SIZE));
  }, [currentAccount, dispatch, wiresListTotal]);

  useEffect(() => {
    if (currentAccount?._id) {
      refreshWires();
      dispatch(payoutThunks.getPaymentMethodsTC(currentAccount._id));
      // balances (PayDue = available for pay-out) are loaded by Account Overview; load here too
      if (extensions === null) {
        dispatch(accountThunks.setAccountExtensionsTC(currentAccount._id));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccount, dispatch, refreshWires]);

  // auto-payout state + available amount; re-read when balances arrive
  useEffect(() => {
    if (currentAccount?._id) {
      dispatch(payoutThunks.getPayoutSettingsTC(currentAccount._id));
    }
  }, [currentAccount, extensions, dispatch]);

  const openDetails = useCallback(
    (wire) => showModal(<WireDetailsModal wire={wire} onClose={hideModal} />),
    [showModal, hideModal]
  );

  const showRowOverlay = useCallback(
    (row) => {
      const wire = row.original?._wire;
      if (!wire || !wire._id) return null;

      const handleClick = (e) => {
        e.stopPropagation();
        openDetails(wire);
      };

      const isDesktop = window.innerWidth >= 640;
      const renderPart = (
        <td className="hover-content" onClick={handleClick}>
          <div className="three-dots-icon">
            <img
              src={`${process.env.PUBLIC_URL}/icons/more-vertical.svg`}
              alt="more options"
              className="three-dots-icon-img"
            />
          </div>
        </td>
      );

      if (isDesktop) return renderPart;
      return <div className="relative-wrapper">{renderPart}</div>;
    },
    [openDetails]
  );

  // Created dates of automatic pay-outs are shown as "June 2026"
  const { tableData, monthOnlyDates } = useMemo(() => {
    const monthOnly = new Set();
    if (!wiresList || !Array.isArray(wiresList)) return { tableData: [], monthOnlyDates: monthOnly };

    const rows = wiresList.map((wire) => {
      const type = getWireTypeDisplay(wire);
      const status = getWireStatusDisplay(wire);
      const createdDate = getWireCreatedDate(wire) || new Date().toISOString();
      if (type.isAuto) monthOnly.add(createdDate);

      return {
        Type: (
          <span className="type-cell-with-icon">
            <img src={type.iconPath} alt={type.label} className={`type-icon ${type.iconClass}`} />
            <span className="type-text">
              {type.title}
              {type.subtitle && <small>{type.subtitle}</small>}
            </span>
          </span>
        ),
        Amount: formatWireAmount(getWireAmount(wire)),
        Status: (
          <span className={`status-cell-with-icon ${status.className}`}>
            <img src={status.iconPath} alt={status.text} className="status-icon" />
            {status.text}
          </span>
        ),
        Created: createdDate,
        _wire: wire,
      };
    });
    return { tableData: rows, monthOnlyDates: monthOnly };
  }, [wiresList]);

  const formatCreated = useCallback(
    (value) => formatWireDate(value, { monthOnly: monthOnlyDates.has(value) }),
    [monthOnlyDates]
  );

  // --- Manual pay-out --------------------------------------------------------
  const handleRequestPayout = () => {
    const active = getActiveManualPayout(wiresList);
    if (active) {
      setActivePayout(active);
    } else {
      setShowPayoutRequestModal(true);
    }
  };

  // --- Automatic payout -----------------------------------------------------
  const autoPayout = payoutSettings?.autoPayout;

  const handleDisableAutoPayout = async () => {
    if (!currentAccount?._id) return;
    try {
      await dispatch(
        payoutThunks.updateAutoPayoutTC(currentAccount._id, { ...(autoPayout || {}), enabled: false })
      );
    } finally {
      setShowDisableModal(false);
    }
  };

  // --- Widget states ---------------------------------------------------------
  // The auto-payout setting and the current month's request are independent:
  // the open request is shown even when the setting is disabled.
  const currentAutoPayout = useMemo(() => getCurrentAutoPayout(wiresList), [wiresList]);
  const lastDeclinedAuto = useMemo(() => getLastDeclinedAutoPayout(wiresList), [wiresList]);
  const pendingDeposits = useMemo(() => getPendingDeposits(wiresList), [wiresList]);
  const activeManualPayout = useMemo(() => getActiveManualPayout(wiresList), [wiresList]);
  const payoutActive = useMemo(() => hasActivePayout(wiresList), [wiresList]);
  const bankAccount = paymentMethods?.bankAccount || null;

  return (
    <div className="wire-management-wrapper">
      {loading && <Spinner className="spinner" animation="grow" />}

      <div className="wire-widgets-row">
        <AutomaticPayoutWidget
          autoPayout={autoPayout}
          currentAutoPayout={currentAutoPayout}
          lastDeclined={lastDeclinedAuto}
          onEnable={() => setShowAutoPayoutModal(true)}
          onEdit={() => setShowAutoPayoutModal(true)}
          onDisable={() => setShowDisableModal(true)}
        />
        <DepositConfirmationWidget
          pendingDeposits={pendingDeposits}
          onConfirm={() => setShowWireConfirmationModal(true)}
        />
        <RequestPayoutWidget
          availableForPayout={payoutSettings?.availableForPayout}
          hasBankAccount={!!bankAccount}
          activeManualPayout={activeManualPayout}
          onRequest={handleRequestPayout}
          onViewRequest={() => activeManualPayout && openDetails(activeManualPayout)}
        />
        <BankDetailsWidget
          bankAccount={bankAccount}
          payoutActive={payoutActive}
          onOpen={() => navigate(TO_BANK_ACCOUNT_DETAILS)}
        />
      </div>

      <div className="wire-management-content">
        <div className="wire-management-actions">
          <span className="c-grey-text">
            <strong>Operations</strong>
          </span>
        </div>

        <div className="wire-management-table">
          <div className="wire-management-table-body">
            <TableComponent
              data={tableData}
              dateColumns={["Created"]}
              formattingForDateColumns={formatCreated}
              showSearch={false}
              itemsToUseSorting={["Type", "Amount", "Status", "Created"]}
              setCellClassName={(cell) => {
                if (cell.column.id === "Type") return "type-cell";
                if (cell.column.id === "Status") return "status-cell";
                return "";
              }}
              showRowOverlay={showRowOverlay}
              defaultSortBy={[{ id: "Created", desc: true }]}
            />
          </div>
          <WireManagementStateController
            loaded={tableData.length}
            total={wiresListTotal || tableData.length}
            pageSize={PAGE_SIZE}
            loading={loading}
            onShowMore={handleShowMore}
            onShowAll={handleShowAll}
          />
        </div>
      </div>

      {showWireConfirmationModal && (
        <WireConfirmationModal
          onClose={() => setShowWireConfirmationModal(false)}
          onSuccess={refreshWires}
        />
      )}

      {showPayoutRequestModal && (
        <PayoutRequestModal
          onClose={() => setShowPayoutRequestModal(false)}
          onViewTransaction={openDetails}
        />
      )}

      {activePayout && (
        <ActivePayoutModal
          wire={activePayout}
          onView={(wire) => {
            setActivePayout(null);
            openDetails(wire);
          }}
          onClose={() => setActivePayout(null)}
        />
      )}

      {showAutoPayoutModal && (
        <AutoPayoutSettingModal onClose={() => setShowAutoPayoutModal(false)} />
      )}

      {showDisableModal && (
        <DisableAutoPayoutModal
          onConfirm={handleDisableAutoPayout}
          onClose={() => setShowDisableModal(false)}
        />
      )}
    </div>
  );
};

export default WireManagementPage;
